import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { updateCompanies, setActiveCompany } from '../store/slices/authSlice';

const CompanyContext = createContext();

export const CompanyProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { companies: reduxCompanies, activeCompany } = useSelector((state) => state.auth);
  const [currentCompany, setCurrentCompany] = useState(null);

  useEffect(() => {
    // Set current company based on activeCompany in Redux
    if (activeCompany && reduxCompanies.length > 0) {
      const company = reduxCompanies.find(c => c.id === activeCompany);
      setCurrentCompany(company || reduxCompanies[0]);
    } else if (reduxCompanies.length > 0) {
      setCurrentCompany(reduxCompanies[0]);
    }
  }, [activeCompany, reduxCompanies]);

  const switchCompany = (companyId) => {
    const company = reduxCompanies.find(c => c.id === companyId);
    if (company) {
      dispatch(setActiveCompany(companyId));
      setCurrentCompany(company);
      
      // Refresh data for the new company
      window.dispatchEvent(new CustomEvent('company-changed', { detail: companyId }));
    }
  };

  const updateCompanyList = (newCompanies) => {
    dispatch(updateCompanies(newCompanies));
  };

  const getCompanyPermissions = () => {
    return currentCompany?.permissions || {};
  };

  const hasPermission = (permission) => {
    return currentCompany?.permissions?.[permission] || false;
  };

  const isCompanyOwner = () => {
    return currentCompany?.role === 'owner';
  };

  return (
    <CompanyContext.Provider
      value={{
        currentCompany,
        companies: reduxCompanies,
        switchCompany,
        updateCompanyList,
        getCompanyPermissions,
        hasPermission,
        isCompanyOwner,
        isMultiCompany: reduxCompanies.length > 1,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyProvider');
  }
  return context;
};